import { fetchChart } from "../network/chartFetch";
import {
  searchFileName,
  type PackDescriptor,
} from "./manifest";
import {
  deletePackFile,
  openPackForWrite,
  readPackFile,
} from "./opfs";

const DATABASE_NAME = "klint-search";
const DATABASE_VERSION = 1;
const FEATURES_STORE = "features";
const PACKS_STORE = "packs";
const RESULT_LIMIT = 30;
const CANDIDATE_LIMIT = 500;
let contextCache: SearchRecord[] | null = null;

export type SearchFeatureKind =
  | "city"
  | "town"
  | "village"
  | "locality"
  | "water"
  | "sea"
  | "land"
  | "region"
  | "harbour"
  | "light"
  | "navigation-mark"
  | "wreck"
  | "rock"
  | "anchorage"
  | "map-feature"
  | "chart-feature";

type SearchTupleV1 = [name: string, longitude: number, latitude: number, aliases?: string];
type SearchTupleV2 = [
  name: string,
  longitude: number,
  latitude: number,
  aliases: string,
  kind: SearchFeatureKind,
  countryCode: string,
  population?: number,
];

interface SearchFile {
  version: 1 | 2;
  pack: string;
  entries: SearchTupleV1[] | SearchTupleV2[];
}

interface SearchRecord {
  id: string;
  packId: string;
  name: string;
  longitude: number;
  latitude: number;
  normalised: string;
  words: string[];
  kind?: SearchFeatureKind;
  countryCode?: string;
  population?: number;
}

interface IndexedPack {
  packId: string;
  bytes: number;
  entries: number;
}

export interface SearchResult {
  packId: string;
  name: string;
  longitude: number;
  latitude: number;
  kind?: SearchFeatureKind;
  countryCode?: string;
  nearbyName?: string;
  areaName?: string;
}

export interface EnsureSearchOptions {
  allowNetwork?: boolean;
  signal?: AbortSignal;
  onProgress?: (received: number, total: number) => void;
}

export function normaliseSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.addEventListener("success", () => resolve(request.result), { once: true });
    request.addEventListener(
      "error",
      () => reject(request.error ?? new Error("IndexedDB request failed.")),
      { once: true },
    );
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.addEventListener("complete", () => resolve(), { once: true });
    transaction.addEventListener(
      "abort",
      () => reject(transaction.error ?? new Error("Search index transaction aborted.")),
      { once: true },
    );
    transaction.addEventListener(
      "error",
      () => reject(transaction.error ?? new Error("Search index transaction failed.")),
      { once: true },
    );
  });
}

async function openDatabase(): Promise<IDBDatabase> {
  const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
  request.addEventListener("upgradeneeded", () => {
    const database = request.result;
    const features = database.createObjectStore(FEATURES_STORE, { keyPath: "id" });
    features.createIndex("packId", "packId");
    features.createIndex("words", "words", { multiEntry: true });
    database.createObjectStore(PACKS_STORE, { keyPath: "packId" });
  });
  return requestResult(request);
}

const SEARCH_KINDS = new Set<SearchFeatureKind>([
  "city",
  "town",
  "village",
  "locality",
  "water",
  "sea",
  "land",
  "region",
  "harbour",
  "light",
  "navigation-mark",
  "wreck",
  "rock",
  "anchorage",
  "map-feature",
  "chart-feature",
]);

function hasValidCoordinates(value: unknown[]): boolean {
  return (
    typeof value[0] === "string" &&
    value[0].trim().length > 0 &&
    value[0].length <= 256 &&
    typeof value[1] === "number" &&
    Number.isFinite(value[1]) &&
    value[1] >= -180 &&
    value[1] <= 180 &&
    typeof value[2] === "number" &&
    Number.isFinite(value[2]) &&
    value[2] >= -90 &&
    value[2] <= 90
  );
}

function isSearchTupleV1(value: unknown): value is SearchTupleV1 {
  if (!Array.isArray(value) || (value.length !== 3 && value.length !== 4)) return false;
  return (
    hasValidCoordinates(value) &&
    (value.length === 3 || (typeof value[3] === "string" && value[3].length <= 2_048))
  );
}

function isSearchTupleV2(value: unknown): value is SearchTupleV2 {
  if (!Array.isArray(value) || (value.length !== 6 && value.length !== 7)) return false;
  return (
    hasValidCoordinates(value) &&
    typeof value[3] === "string" &&
    value[3].length <= 2_048 &&
    typeof value[4] === "string" &&
    SEARCH_KINDS.has(value[4] as SearchFeatureKind) &&
    typeof value[5] === "string" &&
    (value[5] === "" || /^[A-Z]{2}$/.test(value[5])) &&
    (value.length === 6 ||
      (typeof value[6] === "number" && Number.isSafeInteger(value[6]) && value[6] > 0))
  );
}

function parseSearchFile(pack: PackDescriptor, text: string): SearchFile {
  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch {
    throw new Error(`The search index for ${pack.label} is not valid JSON.`);
  }
  if (!value || typeof value !== "object") {
    throw new Error(`The search index for ${pack.label} is invalid.`);
  }
  const file = value as Partial<SearchFile>;
  if (
    (file.version !== 1 && file.version !== 2) ||
    file.pack !== pack.id ||
    !Array.isArray(file.entries) ||
    !(file.version === 1
      ? file.entries.every(isSearchTupleV1)
      : file.entries.every(isSearchTupleV2)) ||
    file.entries.length !== pack.search?.entries
  ) {
    throw new Error(`The search index for ${pack.label} does not match its catalogue entry.`);
  }
  return file as SearchFile;
}

async function indexedPack(packId: string): Promise<IndexedPack | undefined> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(PACKS_STORE, "readonly");
    return await requestResult(
      transaction.objectStore(PACKS_STORE).get(packId) as IDBRequest<IndexedPack | undefined>,
    );
  } finally {
    database.close();
  }
}

async function deletePackRecords(store: IDBObjectStore, packId: string): Promise<void> {
  const index = store.index("packId");
  await new Promise<void>((resolve, reject) => {
    const request = index.openKeyCursor(IDBKeyRange.only(packId));
    request.addEventListener("error", () => reject(request.error), { once: true });
    request.addEventListener("success", () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }
      store.delete(cursor.primaryKey);
      cursor.continue();
    });
  });
}

export async function importPackSearch(pack: PackDescriptor): Promise<boolean> {
  if (!pack.search) return false;
  const raw = await readPackFile(searchFileName(pack));
  if (!raw || raw.size !== pack.search.bytes) return false;
  const current = await indexedPack(pack.id);
  if (current?.bytes === pack.search.bytes && current.entries === pack.search.entries) {
    return true;
  }

  const file = parseSearchFile(pack, await raw.text());
  const records: SearchRecord[] = file.entries.map((entry, ordinal) => {
    const aliases = entry[3]?.split("\u001f").join(" ") ?? "";
    const normalised = normaliseSearchText(`${entry[0]} ${aliases}`);
    const metadata = file.version === 2 ? (entry as SearchTupleV2) : null;
    return {
      id: `${pack.id}:${ordinal}`,
      packId: pack.id,
      name: entry[0],
      longitude: entry[1],
      latitude: entry[2],
      normalised,
      words: [...new Set(normalised.split(" ").filter(Boolean))],
      kind: metadata?.[4],
      countryCode: metadata?.[5] || undefined,
      population: metadata?.[6],
    };
  });

  const database = await openDatabase();
  try {
    const transaction = database.transaction([FEATURES_STORE, PACKS_STORE], "readwrite");
    const features = transaction.objectStore(FEATURES_STORE);
    await deletePackRecords(features, pack.id);
    for (const record of records) features.put(record);
    transaction.objectStore(PACKS_STORE).put({
      packId: pack.id,
      bytes: pack.search.bytes,
      entries: pack.search.entries,
    } satisfies IndexedPack);
    await transactionDone(transaction);
    contextCache = null;
  } finally {
    database.close();
  }
  return true;
}

export async function ensurePackSearch(
  pack: PackDescriptor,
  options: EnsureSearchOptions = {},
): Promise<boolean> {
  if (!pack.search) return false;
  const existing = await readPackFile(searchFileName(pack));
  if (existing?.size === pack.search.bytes) return importPackSearch(pack);
  if (!(options.allowNetwork ?? true)) return false;

  const response = await fetchChart(pack.search.file, {
    signal: options.signal,
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(`Search index download failed for ${pack.label} (${response.status}).`);
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  if (bytes.byteLength !== pack.search.bytes) {
    throw new Error(
      `${pack.label} search index downloaded ${bytes.byteLength} bytes but the catalogue expects ${pack.search.bytes}.`,
    );
  }
  const writable = await openPackForWrite(searchFileName(pack), { keepExisting: false });
  try {
    await writable.write(bytes);
    await writable.close();
  } catch (error) {
    await writable.abort().catch(() => undefined);
    throw error;
  }
  options.onProgress?.(bytes.byteLength, pack.search.bytes);
  return importPackSearch(pack);
}

export async function ensureStoredSearchIndexes(
  packs: PackDescriptor[],
): Promise<Set<string>> {
  const ready = new Set<string>();
  for (const pack of packs) {
    if (!pack.search) continue;
    try {
      if (await ensurePackSearch(pack, { allowNetwork: false })) ready.add(pack.id);
    } catch (error) {
      console.warn(`klint: could not import search index for ${pack.id}:`, error);
    }
  }
  return ready;
}

export async function removePackSearch(pack: PackDescriptor): Promise<void> {
  await deletePackFile(searchFileName(pack));
  const database = await openDatabase();
  try {
    const transaction = database.transaction([FEATURES_STORE, PACKS_STORE], "readwrite");
    await deletePackRecords(transaction.objectStore(FEATURES_STORE), pack.id);
    transaction.objectStore(PACKS_STORE).delete(pack.id);
    await transactionDone(transaction);
    contextCache = null;
  } finally {
    database.close();
  }
}

function score(record: SearchRecord, query: string, terms: string[]): number {
  const primary = normaliseSearchText(record.name);
  if (primary === query) return 0;
  if (primary.startsWith(query)) return 1;
  if (terms.every((term) => primary.split(" ").some((word) => word.startsWith(term)))) return 2;
  if (record.normalised.includes(query)) return 3;
  return 4;
}

const PLACE_KINDS = new Set<SearchFeatureKind>(["city", "town", "village", "locality"]);
const AREA_KINDS = new Set<SearchFeatureKind>(["water", "sea", "land", "region"]);

async function contextRecords(database: IDBDatabase): Promise<SearchRecord[]> {
  if (contextCache) return contextCache;
  const transaction = database.transaction(FEATURES_STORE, "readonly");
  const records = await requestResult(
    transaction.objectStore(FEATURES_STORE).getAll() as IDBRequest<SearchRecord[]>,
  );
  contextCache = records.filter(
    (record) => !!record.kind && (PLACE_KINDS.has(record.kind) || AREA_KINDS.has(record.kind)),
  );
  return contextCache;
}

function distanceKm(a: SearchRecord, b: SearchRecord): number {
  const latitude = ((a.latitude + b.latitude) / 2) * (Math.PI / 180);
  const eastWest = (a.longitude - b.longitude) * Math.cos(latitude);
  const northSouth = a.latitude - b.latitude;
  return Math.hypot(eastWest, northSouth) * 111.2;
}

function closestContext(
  target: SearchRecord,
  contexts: SearchRecord[],
  installed: Set<string>,
  kinds: Set<SearchFeatureKind>,
): SearchRecord | undefined {
  let best: SearchRecord | undefined;
  let bestScore = Infinity;
  const targetName = normaliseSearchText(target.name);
  for (const context of contexts) {
    if (
      !context.kind ||
      !kinds.has(context.kind) ||
      !installed.has(context.packId) ||
      context.id === target.id ||
      (target.countryCode &&
        context.countryCode &&
        target.countryCode !== context.countryCode) ||
      normaliseSearchText(context.name) === targetName
    ) {
      continue;
    }
    const distance = distanceKm(target, context);
    const limit =
      context.kind === "city" || context.kind === "region" || context.kind === "sea"
        ? 160
        : context.kind === "town"
          ? 90
          : context.kind === "land" || context.kind === "water"
            ? 60
            : 30;
    if (distance > limit) continue;
    const prominence =
      context.kind === "city"
        ? 0.72
        : context.kind === "town" || context.kind === "region" || context.kind === "sea"
          ? 0.84
          : context.kind === "village" || context.kind === "land"
            ? 0.95
            : 1;
    const populationBonus = context.population
      ? Math.min(6, Math.log10(context.population + 1)) * 0.35
      : 0;
    const candidateScore = distance * prominence - populationBonus;
    if (candidateScore < bestScore) {
      best = context;
      bestScore = candidateScore;
    }
  }
  return best;
}

export async function searchStoredMaps(
  packs: PackDescriptor[],
  value: string,
): Promise<SearchResult[]> {
  const query = normaliseSearchText(value);
  if (query.length < 2) return [];
  const terms = query.split(" ").filter(Boolean);
  const first = [...terms].sort((a, b) => b.length - a.length)[0];
  const installed = new Set(packs.map((pack) => pack.id));
  const candidates = new Map<string, SearchRecord>();
  let contexts: SearchRecord[] = [];
  const database = await openDatabase();
  try {
    const transaction = database.transaction(FEATURES_STORE, "readonly");
    const words = transaction.objectStore(FEATURES_STORE).index("words");
    const range = IDBKeyRange.bound(first, `${first}\uffff`);
    await new Promise<void>((resolve, reject) => {
      const request = words.openCursor(range);
      request.addEventListener("error", () => reject(request.error), { once: true });
      request.addEventListener("success", () => {
        const cursor = request.result;
        if (!cursor || candidates.size >= CANDIDATE_LIMIT) {
          resolve();
          return;
        }
        const record = cursor.value as SearchRecord;
        if (
          installed.has(record.packId) &&
          terms.every((term) => record.normalised.split(" ").some((word) => word.startsWith(term)))
        ) {
          candidates.set(record.id, record);
        }
        cursor.continue();
      });
    });
    if (candidates.size) contexts = await contextRecords(database);
  } finally {
    database.close();
  }

  return [...candidates.values()]
    .sort((a, b) => score(a, query, terms) - score(b, query, terms) || a.name.localeCompare(b.name))
    .slice(0, RESULT_LIMIT)
    .map((record) => {
      const nearby = PLACE_KINDS.has(record.kind ?? "map-feature")
        ? undefined
        : closestContext(record, contexts, installed, PLACE_KINDS);
      const area =
        PLACE_KINDS.has(record.kind ?? "map-feature") ||
        AREA_KINDS.has(record.kind ?? "map-feature")
        ? undefined
        : closestContext(record, contexts, installed, AREA_KINDS);
      return {
        packId: record.packId,
        name: record.name,
        longitude: record.longitude,
        latitude: record.latitude,
        kind: record.kind,
        countryCode: record.countryCode ?? nearby?.countryCode ?? area?.countryCode,
        nearbyName: nearby?.name,
        areaName: area?.name,
      };
    });
}
